import {HttpClient, HttpResponse} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {SwUpdate} from '@angular/service-worker';
import moment from 'moment';
import {defer, EMPTY, merge, Observable, of, race, Subject, timer} from 'rxjs';
import {catchError, map, repeat, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {AppConfigService} from '../../services/config.service';
import {LocalStorageService} from '../../services/local-storage.service';
import {UserService} from '../../services/user.service';
import {destroy$} from '../destroyable';
import {LoggerService} from '../logger/logger.service';
import {ModalService} from '../modal';
import {AlertModalComponent} from '../modal/alert-modal';

/**
 * Deployment ID request interval.
 */
const DEPLOYMENT_VALIDATION_INTERVAL = 1000 * 10 * 4; // 40 secs

const APP_RELOADED_TIP_STORAGE_KEY = 'appIsReloaded';

/**
 * Amount of milliseconds after that the stored info about the last reloading will be ignored.
 */
const APP_RELOADED_TIP_STORAGE_EXPIRATION_TIME = 1000 * 20; // 20 seconds.

enum AppReloadedReason {
    REDEPLOYED = 'REDEPLOYED'
}
const APP_RELOADED_TITLE = 'components.application-reload.application_reloaded_title';
const APP_RELOADED_MESSAGES = {
    [AppReloadedReason.REDEPLOYED]: 'components.application-reload.application_reloaded_due_redeployment'
};

/**
 * The last reloading info.
 * Object of storage between reloads.
 */
interface AppReloadedStorableTip {
    reason: AppReloadedReason;
    time: string;
}

@Injectable()
export class ApplicationReloadService {

    public readonly noVersionDetected$: Observable<void>;
    public readonly versionDetected$: Observable<void>;
    public readonly versionReady$: Observable<void>;
    public readonly versionInstallationFailed$: Observable<void>;

    private readonly httpClient = inject(HttpClient);
    private readonly localStorageService = inject(LocalStorageService);
    private readonly modalService = inject(ModalService);
    private readonly appConfigService = inject(AppConfigService);
    private readonly swUpdate = inject(SwUpdate);
    private readonly loggerService = inject(LoggerService);
    private readonly userService = inject(UserService);

    private deploymentIdApiUrl: string = this.appConfigService.getPublicPath('api/deployment_id');
    private deploymentId: string;

    private isInitialized: boolean = false;
    private readonly destroy$ = destroy$();

    private readonly forceCheckForUpdates$ = new Subject<void>();

    private readonly noVersionDetectedSubject$ = new Subject<void>();
    private readonly versionDetectedSubject$ = new Subject<void>();
    private readonly versionReadySubject$ = new Subject<void>();
    private readonly versionInstallationFailedSubject$ = new Subject<void>();

    constructor() {
        this.noVersionDetected$ = this.noVersionDetectedSubject$.asObservable();
        this.versionDetected$ = this.versionDetectedSubject$.asObservable();
        this.versionReady$ = this.versionReadySubject$.asObservable();
        this.versionInstallationFailed$ = this.versionInstallationFailedSubject$.asObservable();
    }

    public init(): void {
        if (this.isInitialized || !this.isUpdatesCheckEnabled()) {
            return;
        }

        this.checkIfAppReloaded();

        const swIsNotChecking$ = new Subject<void>();

        const checkDelay$ = (
            race(
                timer(DEPLOYMENT_VALIDATION_INTERVAL),
                this.forceCheckForUpdates$
            ).pipe(
                take(1)
            )
        );

        const checkByTimer$ = defer(() => {
            let doRepeat: boolean = true;

            return checkDelay$.pipe(
                repeat({
                    delay: () => doRepeat ? of(undefined) : EMPTY
                }),
                switchMap(() => this.validateDeployment()),
                tap(isActual => {
                    doRepeat = isActual;

                    if (!isActual) {
                        this.handleNewDeploymentId();
                    }
                })
            );
        });

        merge(
            swIsNotChecking$.pipe(
                switchMap(() => checkByTimer$)
            ),
            this.listenToSwEvents(swIsNotChecking$)
        ).pipe(
            takeUntil(this.destroy$)
        ).subscribe();

        // Initiate the first check if SW is not ready.
        // Otherwise, it's initiated by the SW.
        if (!this.isSwEnabled()) {
            swIsNotChecking$.next();
        }

        this.isInitialized = true;
    }

    public forceCheckForUpdates(): void {
        this.forceCheckForUpdates$.next();
    }

    private checkIfAppReloaded(): void {
        const tip: AppReloadedStorableTip = this.restoreAppReloadedTip();

        if (tip) {
            this.showAppReloadedAlert(tip);
        }
    }

    private isSwEnabled(): boolean {
        return this.swUpdate.isEnabled;
    }

    private isUpdatesCheckEnabled(): boolean {
        return this.appConfigService.appConfig.production;
    }

    private validateDeployment(): Observable<boolean> {
        return this.httpClient.get(this.deploymentIdApiUrl, {
            observe: 'response',
            responseType: 'text'
        }).pipe(
            map((response: HttpResponse<string>) => response.body),
            map((deploymentId: string): boolean => {
                const currentDeploymentId: string = this.deploymentId;
                const isActual: boolean = !currentDeploymentId
                    || !deploymentId
                    || currentDeploymentId === deploymentId;

                this.deploymentId = deploymentId;

                if (!isActual) {
                    this.loggerService.log({
                        event: '[AppReload] New deploymentId found',
                        userLogin: this.userService.user?.login
                    });
                }

                return isActual;
            }),
            catchError(() => of(true)) // assume that we are in actual state in case of any error
        );
    }

    private reload(): void {
        this.storeAppReloadedTip();
        location.reload();
    }

    private handleNewDeploymentId(): void {

        if (!this.isSwEnabled()) {
            this.handleNewVersionIsReady();
        } else {
            this.swUpdate.checkForUpdate().catch();
        }
    }

    private handleNewVersionIsReady(): void {
        this.reload();
    }

    private storeAppReloadedTip(): void {
        const tip: AppReloadedStorableTip = {
            reason: AppReloadedReason.REDEPLOYED,
            time: moment().toISOString()
        };

        this.localStorageService.set(APP_RELOADED_TIP_STORAGE_KEY, tip);
    }

    private restoreAppReloadedTip(): AppReloadedStorableTip {
        let tip: AppReloadedStorableTip = null;
        const storedTip: AppReloadedStorableTip = this.localStorageService.get(APP_RELOADED_TIP_STORAGE_KEY);

        if (storedTip && storedTip.time) {
            const tipTime: moment.Moment = moment(storedTip.time);

            if (moment().diff(tipTime) < APP_RELOADED_TIP_STORAGE_EXPIRATION_TIME) {
                tip = storedTip;
            }
        }

        this.localStorageService.remove(APP_RELOADED_TIP_STORAGE_KEY);

        return tip;
    }

    private showAppReloadedAlert(tip: AppReloadedStorableTip): void {
        const alertMessage: string = APP_RELOADED_MESSAGES[tip.reason] || APP_RELOADED_TITLE;

        this.loggerService.log({
            event: '[AppReload] showAppReloadedAlert',
            userLogin: this.userService.user?.login
        });

        this.modalService.open(AlertModalComponent, {
            configure: {
                alertTitle: APP_RELOADED_TITLE,
                alertMessage
            }
        });
    }

    private listenToSwEvents(swIsNotChecking$: Subject<void>): Observable<unknown> {
        return this.swUpdate.versionUpdates.pipe(
            tap(event => {
                switch (event.type) {
                    case 'VERSION_DETECTED': {
                        this.emitVersionDetected();
                        break;
                    }
                    case 'VERSION_READY': {
                        this.emitVersionReady();
                        this.handleNewVersionIsReady();
                        swIsNotChecking$.next();
                        break;
                    }
                    case 'VERSION_INSTALLATION_FAILED': {
                        this.emitVersionInstallationFailed(event.error);
                        swIsNotChecking$.next();
                        break;
                    }
                    case 'NO_NEW_VERSION_DETECTED': {
                        this.emitNoVersionDetected();
                        swIsNotChecking$.next();
                    }
                }
            })
        );
    }

    private emitNoVersionDetected(): void {
        this.noVersionDetectedSubject$.next();
    }

    private emitVersionDetected(): void {
        this.versionDetectedSubject$.next();
    }

    private emitVersionReady(): void {
        this.loggerService.log({
            event: '[AppReload] versionReady',
            userLogin: this.userService.user?.login
        });

        this.versionReadySubject$.next();
    }

    private emitVersionInstallationFailed(error: string): void {
        this.loggerService.log({
            event: '[AppReload] versionInstallationFailed',
            userLogin: this.userService.user?.login,
            error
        });

        this.versionInstallationFailedSubject$.next();
    }

}
